import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useReveal } from '../hooks';
import ArrowIcon from '../components/ArrowIcon';
import Footer from '../components/Footer';

const VALUES = [
  {
    number: '01',
    title: 'Engineering Excellence',
    desc: 'We obsess over the details. From hardware schematics to cloud architecture, every system we build is designed for reliability, scalability, and long-term performance in the field.',
    accent: 'var(--red)',
    rgb: '255,0,4',
  },
  {
    number: '02',
    title: 'Full-Spectrum Innovation',
    desc: "We operate across the entire technology stack — firmware, hardware, connectivity, data, AI. This full-spectrum depth lets us solve problems that siloed teams simply can't reach.",
    accent: 'var(--blue)',
    rgb: '0,60,255',
  },
  {
    number: '03',
    title: 'Reliable Delivery',
    desc: 'Every timeline. Every commitment. In engineering, trust is built through predictable execution and radical transparency. Our process is designed to deliver exactly what we promise.',
    accent: 'var(--ink)',
    rgb: '10,10,20',
  },
  {
    number: '04',
    title: 'True Partnership',
    desc: "We don't just build products — we build partnerships. Zone-X embeds with your team, aligns deeply with your goals, and stays accountable to your outcomes from first sketch to final deployment.",
    accent: 'var(--red-deep)',
    rgb: '255,0,72',
  },
];

const STATS = [
  { value: '4+', label: 'Years engineering' },
  { value: '50+', label: 'Products shipped' },

  { value: '6+', label: 'Industries' },
];

const TECH_ROW1 = [
  'IoT', 'Embedded C/C++', 'PCB Design', 'FPGA', 'STM32', 'ESP32',
  'ARM Cortex', 'KiCad', 'Altium Designer', 'RTOS', 'FreeRTOS', 'CAN Bus', 'UART / SPI / I²C', 'Firmware',
];

const TECH_ROW2 = [
  'BLE', 'Zigbee', 'LoRa / LoRaWAN', 'MQTT', 'AWS IoT', 'Azure IoT Hub',
  'InfluxDB', 'Grafana', '4G / LTE', 'NB-IoT', 'OTA Updates', 'Docker', 'Kubernetes', 'Modbus',
];

const TECH_ROW3 = [
  'AI / ML', 'Computer Vision', 'TensorFlow Lite', 'Edge AI', 'React', 'Node.js',
  'Python', 'TypeScript', 'Full-Stack', 'Robotics', 'Sensor Fusion', 'OpenCV', 'Linux / Yocto', 'Edge Computing',
];

export default function AboutPage() {
  useReveal();

  return (
    <>
      <Helmet>
        <title>About Us | Zone-X Laboratories</title>
        <meta name="description" content="Zone-X is an engineering-first company building advanced IoT, embedded systems, AI/ML, and full-stack solutions. 8+ years, 120+ projects shipped, 45 engineers across 12 industries globally." />
        <meta property="og:title" content="About Us | Zone-X Laboratories" />
        <meta property="og:description" content="Engineering-first company at the crossroads of hardware intelligence, embedded systems, and scalable software." />
        <meta property="og:url" content="https://zone-x.dev/about" />
        <link rel="canonical" href="https://zone-x.dev/about" />
      </Helmet>

      {/* ── HERO ── */}
      <section style={{ padding: '152px 0 72px', position: 'relative' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>

          {/* Breadcrumb */}
          <div className="reveal" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 40 }}>
            <Link
              to="/"
              style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--ink-soft)', transition: 'color 200ms ease' }}
              onMouseEnter={e => { e.currentTarget.style.color = 'var(--red)'; }}
              onMouseLeave={e => { e.currentTarget.style.color = 'var(--ink-soft)'; }}
            >
              Home
            </Link>
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" style={{ opacity: 0.3, flexShrink: 0 }}>
              <path d="M5 3l4 4-4 4" stroke="var(--ink)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--red)' }}>About</span>
          </div>

          {/* Tag row */}
          <div className="reveal" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
            <span style={{
              fontFamily: "'Inter', sans-serif",
              fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
              color: 'var(--red)', padding: '5px 14px',
              background: 'rgba(255,0,4,0.06)',
              border: '1px solid rgba(255,0,4,0.18)',
              borderRadius: 999,
            }}>
              Our Story
            </span>
            <div style={{ width: 48, height: 1, background: 'var(--red)', opacity: 0.25 }} />
            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 1, color: 'var(--ink-soft)', opacity: 0.55 }}>EST. 2018</span>
          </div>

          {/* Hero heading */}
          <h1
            className="reveal"
            style={{
              fontFamily: "'Anton', sans-serif",
              fontWeight: 400,
              fontSize: 'clamp(52px, 8vw, 100px)',
              lineHeight: 0.92,
              color: 'var(--ink)',
              marginBottom: 36,
              maxWidth: 860,
            }}
          >
            BUILT BY<br />
            ENGINEERS.<br />
            <span style={{ color: 'var(--red)' }}>FOR ENGINEERS.</span>
          </h1>

          {/* Subtext */}
          <p
            className="reveal"
            style={{ fontSize: 18, lineHeight: 1.75, color: 'var(--ink-soft)', maxWidth: 580, marginBottom: 20 }}
          >
            We are a technology and product engineering company focused on building smart, connected, and reliable products for businesses and industries. We work across IoT, embedded systems, hardware, software, PCB design, cloud integration, and product R&amp;D to help clients turn ideas into real working solutions.
          </p>
          <p
            className="reveal"
            style={{ fontSize: 18, lineHeight: 1.75, color: 'var(--ink-soft)', maxWidth: 580, marginBottom: 60 }}
          >
            For the past 4+ years, we have been helping businesses build better products with a practical, problem-solving approach. We have worked with 6+ industrial clients and delivered 50+ products across different use cases and requirements.
          </p>

          {/* Stats row */}
          <div
            className="reveal about-page-stats-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              background: 'var(--line)',
              borderRadius: 20,
              overflow: 'hidden',
              maxWidth: 720,
            }}
          >
            {STATS.map(s => (
              <div key={s.label} style={{ background: 'rgba(255,255,255,0.85)', padding: '24px 20px', backdropFilter: 'blur(12px)' }}>
                <div style={{ fontFamily: "'Anton', sans-serif", fontWeight: 400, fontSize: 40, color: 'var(--ink)', lineHeight: 1 }}>
                  {s.value}
                </div>
                <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, color: 'var(--ink-soft)', textTransform: 'uppercase', marginTop: 6, lineHeight: 1.4 }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── MISSION ── */}
      <section style={{ padding: '72px 0' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
          <div
            className="reveal about-page-mission-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
              gap: 64,
              alignItems: 'start',
              background: 'linear-gradient(135deg, rgba(255,255,255,0.72), rgba(255,255,255,0.38))',
              border: '1px solid var(--line)',
              borderRadius: 28,
              padding: '56px',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {/* Left: mission quote */}
            <div>
              <p style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: 'clamp(20px, 2.6vw, 30px)',
                fontWeight: 700,
                lineHeight: 1.42,
                color: 'var(--ink)',
                marginBottom: 32,
              }}>
                "The future of innovation belongs to teams that can turn complex ideas into practical, scalable, and intelligent products."
              </p>
              <div style={{ height: 2, background: 'linear-gradient(90deg, var(--red), transparent)', width: 80, marginBottom: 28 }} />
              <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--ink-soft)' }}>
                Zone-X was founded on this belief. We are not a software agency, not a consulting firm — we are an
                engineering studio with deep roots in both hardware and software, united by a relentless obsession
                with building things that work reliably in the real world.
              </p>
            </div>

            {/* Right: two info blocks */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
              <div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  color: 'var(--red)', padding: '4px 12px',
                  background: 'rgba(255,0,4,0.06)', border: '1px solid rgba(255,0,4,0.15)', borderRadius: 999,
                }}>
                  How We Work
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--ink-soft)' }}>
                  Every project at Zone-X starts with understanding the client's goal, challenge, and product
                  requirements. From there, we research the right approach, design the solution, build the
                  product, and test it carefully before delivery. This process helps us create products that
                  are practical, scalable, and ready for real-world use.
                </p>
              </div>

              <div style={{ height: 1, background: 'var(--line)' }} />

              <div>
                <div style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 14,
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 10, fontWeight: 700, letterSpacing: 2, textTransform: 'uppercase',
                  color: 'var(--blue)', padding: '4px 12px',
                  background: 'rgba(0,60,255,0.06)', border: '1px solid rgba(0,60,255,0.15)', borderRadius: 999,
                }}>
                  Why Clients Trust Zone-X
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--ink-soft)' }}>
                  Clients choose Zone-X because we bring multiple areas of engineering together in one place.
                  Instead of working with separate teams for hardware, software, and product development, they
                  get one trusted partner who can handle the full journey.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── MISSION & VISION ── */}
      <section style={{ padding: '72px 0' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
          <div className="reveal" style={{ marginBottom: 48 }}>
            <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 2.5, color: 'var(--red)', textTransform: 'uppercase' }}>
              / purpose
            </span>
            <h2 style={{ fontFamily: "'Anton', sans-serif", fontWeight: 400, fontSize: 'clamp(38px, 5.5vw, 62px)', color: 'var(--ink)', marginTop: 8, lineHeight: 1 }}>
              MISSION &amp; VISION
            </h2>
          </div>

          <div className="reveal about-page-mv-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 24 }}>
            <MissionVisionCard
              label="Our Mission"
              accent="var(--red)"
              rgb="255,0,4"
              icon={
                <path d="M12 2l2.4 7.2H22l-6 4.4 2.3 7.2L12 16.4 5.7 20.8 8 13.6 2 9.2h7.6L12 2z" />
              }
              text="Our mission is to help businesses build smart technology products that solve real problems and create lasting impact. We aim to be a trusted engineering partner for companies looking for IoT solutions, embedded systems, hardware development, software development, and R&D support."
            />
            <MissionVisionCard
              label="Our Vision"
              accent="var(--blue)"
              rgb="0,60,255"
              icon={
                <path d="M12 5c-7 0-10 7-10 7s3 7 10 7 10-7 10-7-3-7-10-7zm0 11.5A4.5 4.5 0 1112 7.5a4.5 4.5 0 010 9z" />
              }
              text="We want Zone-X to be known as a reliable technology partner for businesses that want to build the future with smart, connected, and scalable products."
            />
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      {/* <section style={{ padding: '72px 0' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
          <div className="reveal" style={{ marginBottom: 52 }}>
            <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 11, color: 'var(--red)',  textTransform: 'uppercase' }}>
              / core-values
            </span>
            <h2 style={{ fontFamily: "'Anton', sans-serif", fontSize: 'clamp(38px, 5.5vw, 62px)',  color: 'var(--ink)', marginTop: 8, lineHeight: 1 }}>
              WHAT DRIVES US
            </h2>
          </div>

          <div className="about-page-values-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
            {VALUES.map(v => <ValueCard key={v.number} {...v} />)}
          </div>
        </div>
      </section> */}

      {/* ── EXPERTISE ── */}
      <section style={{ padding: '72px 0' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
          <div className="reveal" style={{ marginBottom: 48, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', flexWrap: 'wrap', gap: 20 }}>
            <div>
              <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: 11, letterSpacing: 2.5, color: 'var(--blue)', textTransform: 'uppercase' }}>
                / tech-stack
              </span>
              <h2 style={{ fontFamily: "'Anton', sans-serif", fontWeight: 400, fontSize: 'clamp(38px, 5.5vw, 62px)', color: 'var(--ink)', marginTop: 8, lineHeight: 1 }}>
                OUR EXPERTISE
              </h2>
              <p style={{ fontSize: 15, color: 'var(--ink-soft)', marginTop: 14, lineHeight: 1.75, maxWidth: 500 }}>
                Spanning hardware, connectivity, cloud, and intelligent software — our stack covers the full spectrum of modern engineering.
              </p>
            </div>
            <div style={{
              display: 'flex', gap: 24, alignItems: 'center',
              padding: '14px 24px',
              borderRadius: 16,
              border: '1px solid var(--line)',
              background: 'rgba(255,255,255,0.65)',
              backdropFilter: 'blur(12px)',
              flexShrink: 0,
            }}>
              {[
                { dot: '#FF0004', label: 'Hardware' },
                { dot: '#003CFF', label: 'Connectivity' },
                { dot: '#7C3AFF', label: 'Software & AI' },
              ].map(({ dot, label }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: dot, boxShadow: `0 0 8px ${dot}99`, flexShrink: 0 }} />
                  <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink-soft)' }}>{label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="reveal" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <ExpertiseRow tags={TECH_ROW1} accentHex="#FF0004" accentRgb="255,0,4" trackClass="exp-track-left" />
          <ExpertiseRow tags={TECH_ROW2} accentHex="#003CFF" accentRgb="0,60,255" trackClass="exp-track-right" />
          <ExpertiseRow tags={TECH_ROW3} accentHex="#7C3AFF" accentRgb="124,58,255" trackClass="exp-track-left-b" />
        </div>

        <div style={{ textAlign: 'center', marginTop: 28 }}>
          <span style={{
            fontFamily: "'Inter', sans-serif",
            fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: 'uppercase',
            color: 'var(--ink-soft)', opacity: 0.35,
          }}>
            {TECH_ROW1.length + TECH_ROW2.length + TECH_ROW3.length}+ technologies &amp; counting
          </span>
        </div>
      </section>

      {/* ── CTA ── */}
      {/* <section style={{ padding: '72px 0 120px' }}>
        <div style={{ maxWidth: 1320, margin: '0 auto', padding: '0 32px' }}>
          <div
            className="reveal"
            style={{
              background: 'linear-gradient(135deg, var(--ink) 0%, #1a1a2e 100%)',
              borderRadius: 28,
              padding: 'clamp(48px, 6vw, 88px) clamp(32px, 5vw, 80px)',
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <div aria-hidden style={{
              position: 'absolute', inset: 0, opacity: 0.5,
              background: 'radial-gradient(ellipse at 25% 50%, rgba(255,0,4,0.28), transparent 55%), radial-gradient(ellipse at 78% 50%, rgba(0,60,255,0.18), transparent 55%)',
            }} />
            <div style={{ position: 'relative' }}>
              <p style={{
                fontFamily: "'Anton', sans-serif",
                fontSize: 'clamp(28px, 4.5vw, 58px)',
               
                color: 'white',
                lineHeight: 1.2,
                marginBottom: 16,
                
              }}>
                Ready to build something <span style={{ color: '#ff4d4d' }}>remarkable?</span>
              </p>
              <p style={{ fontSize: 16, color: 'rgba(255,255,255,0.5)', maxWidth: 440, margin: '0 auto 48px' }}>
                Tell us about your project. We typically respond within 24 hours.
              </p>
              <Link
                to="/contact"
                className="hoverable"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 12,
                  padding: '18px 44px',
                  background: 'var(--red)',
                  color: 'white',
                  borderRadius: 999,
                  fontSize: 15, fontWeight: 700, 
                  boxShadow: '0 20px 52px rgba(255,0,4,0.4)',
                  transition: 'transform 200ms ease, box-shadow 200ms ease',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 28px 60px rgba(255,0,4,0.52)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 20px 52px rgba(255,0,4,0.4)';
                }}
              >
                Start a Project
                <ArrowIcon size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section> */}

      <Footer />
    </>
  );
}

function MissionVisionCard({ label, text, accent, rgb, icon }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: hov ? `rgba(${rgb},0.045)` : 'rgba(255,255,255,0.65)',
        border: `1px solid ${hov ? `rgba(${rgb},0.25)` : 'var(--line)'}`,
        borderRadius: 24,
        padding: 'clamp(28px, 3.2vw, 44px)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        transition: 'all 280ms ease',
        transform: hov ? 'translateY(-4px)' : 'translateY(0)',
        cursor: 'default',
      }}
    >
      <div aria-hidden style={{
        position: 'absolute', top: -40, right: -40, width: 140, height: 140, borderRadius: '50%',
        background: `radial-gradient(circle, rgba(${rgb},0.16), transparent 70%)`,
        opacity: hov ? 1 : 0.6, transition: 'opacity 280ms ease',
      }} />

      <div style={{
        width: 52, height: 52, borderRadius: 16,
        background: `rgba(${rgb},0.08)`,
        border: `1px solid rgba(${rgb},0.2)`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 24,
      }}>
        <svg width="24" height="24" viewBox="0 0 24 24" fill={accent}>
          {icon}
        </svg>
      </div>

      <h3 style={{
        fontFamily: "'Anton', sans-serif",
        fontWeight: 400,
        fontSize: 'clamp(20px, 2.2vw, 26px)',
        color: 'var(--ink)',
        marginBottom: 16,
        lineHeight: 1.1,
        textTransform: 'uppercase',
      }}>
        {label}
      </h3>
      <p style={{ fontSize: 15, lineHeight: 1.8, color: 'var(--ink-soft)', position: 'relative' }}>
        {text}
      </p>
    </div>
  );
}

function ValueCard({ number, title, desc, accent, rgb }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      className="reveal"
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? `rgba(${rgb},0.04)` : 'rgba(255,255,255,0.62)',
        border: `1px solid ${hov ? `rgba(${rgb},0.22)` : 'var(--line)'}`,
        borderRadius: 20,
        padding: 'clamp(24px, 3vw, 40px)',
        backdropFilter: 'blur(14px)',
        WebkitBackdropFilter: 'blur(14px)',
        transition: 'all 260ms ease',
        cursor: 'default',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22 }}>
        <span style={{ fontFamily: "'Anton', sans-serif", fontSize: 11, color: accent,  opacity: 0.75 }}>
          {number}
        </span>
        <div style={{
          width: 38, height: 38, borderRadius: '50%',
          background: `rgba(${rgb},0.08)`,
          border: `1px solid rgba(${rgb},0.2)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%', background: accent, opacity: hov ? 1 : 0.7, transition: 'opacity 260ms ease' }} />
        </div>
      </div>
      <h3 style={{
        fontFamily: "'Anton', sans-serif",
        fontSize: 'clamp(20px, 2.2vw, 26px)',
         color: 'var(--ink)', marginBottom: 14, lineHeight: 1.1,
      }}>
        {title.toUpperCase()}
      </h3>
      <p style={{ fontSize: 14, lineHeight: 1.8, color: 'var(--ink-soft)' }}>
        {desc}
      </p>
    </div>
  );
}

function ExpertiseRow({ tags, accentHex, accentRgb, trackClass }) {
  const doubled = [...tags, ...tags];
  return (
    <div className="exp-marquee-row">
      <div className={trackClass}>
        {doubled.map((label, i) => (
          <ExpertiseTag key={i} label={label} accentHex={accentHex} accentRgb={accentRgb} />
        ))}
      </div>
    </div>
  );
}

function ExpertiseTag({ label, accentHex, accentRgb }) {
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 10,
      fontSize: 13,
      fontWeight: 600,
      padding: '12px 24px',
      borderRadius: 999,
      border: `1px solid rgba(${accentRgb}, 0.18)`,
      background: `rgba(${accentRgb}, 0.05)`,
      backdropFilter: 'blur(10px)',
      WebkitBackdropFilter: 'blur(10px)',
      color: 'var(--ink)',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      
    }}>
      <span style={{
        width: 7,
        height: 7,
        borderRadius: '50%',
        background: accentHex,
        flexShrink: 0,
        boxShadow: `0 0 8px ${accentHex}99`,
      }} />
      {label}
    </span>
  );
}
